import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MaintenanceCalendar = ({ equipment, workOrders, selectedDate, onDateChange, onRefresh }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const date = new Date(selectedDate);
    const day = date?.getDay();
    const diff = date?.getDate() - day;
    return new Date(date.setDate(diff));
  });

  const generateWeekDays = () => {
    const days = [];
    const start = new Date(currentWeekStart);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date?.setDate(start?.getDate() + i);
      days?.push(date);
    }
    
    return days;
  };

  const weekDays = generateWeekDays();

  const handlePreviousWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart?.setDate(currentWeekStart?.getDate() - 7);
    setCurrentWeekStart(newStart);
  };

  const handleNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart?.setDate(currentWeekStart?.getDate() + 7);
    setCurrentWeekStart(newStart);
  };

  const handleToday = () => {
    const today = new Date();
    const day = today?.getDay();
    const diff = today?.getDate() - day;
    const weekStart = new Date(today.setDate(diff));
    setCurrentWeekStart(weekStart);
    onDateChange(new Date());
  };

  const getMaintenanceForDate = (date) => {
    const dateStr = date?.toISOString()?.split('T')?.[0];
    return workOrders?.filter(wo => {
      const scheduledDate = wo?.scheduled_date?.split('T')?.[0];
      return scheduledDate === dateStr;
    });
  };

  const getEquipmentForDate = (date) => {
    const dateStr = date?.toISOString()?.split('T')?.[0];
    return equipment?.filter(eq => {
      const nextMaintenance = eq?.next_maintenance_date?.split('T')?.[0];
      return nextMaintenance === dateStr;
    });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-error/10 text-error border-error',
      high: 'bg-warning/10 text-warning border-warning',
      medium: 'bg-primary/10 text-primary border-primary',
      low: 'bg-muted text-muted-foreground border-border'
    };
    return colors?.[priority] || colors?.medium;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Cardio': 'Activity',
      'Strength': 'Dumbbell',
      'HVAC': 'Wind',
      'Electrical': 'Zap',
      'Plumbing': 'Droplet'
    };
    return icons?.[category] || 'Package';
  };

  const formatMonthYear = () => {
    const endOfWeek = new Date(currentWeekStart);
    endOfWeek?.setDate(currentWeekStart?.getDate() + 6);
    
    if (currentWeekStart?.getMonth() === endOfWeek?.getMonth()) {
      return currentWeekStart?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else {
      return `${currentWeekStart?.toLocaleDateString('en-US', { month: 'short' })} - ${endOfWeek?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
    }
  };

  const isToday = (date) => {
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <div className="bg-card rounded-xl border border-border p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">
              {formatMonthYear()}
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleToday}
              iconName="Calendar"
              iconPosition="left"
            >
              Today
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handlePreviousWeek}
              iconName="ChevronLeft"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleNextWeek}
              iconName="ChevronRight"
            />
          </div>
        </div>

        {/* Week View */}
        <div className="grid grid-cols-7 gap-2">
          {weekDays?.map((date, index) => {
            const dayName = date?.toLocaleDateString('en-US', { weekday: 'short' });
            const dayNumber = date?.getDate();
            const today = isToday(date);
            const maintenanceItems = getMaintenanceForDate(date);
            const equipmentItems = getEquipmentForDate(date);
            const totalItems = maintenanceItems?.length + equipmentItems?.length;

            return (
              <div
                key={index}
                className={`flex flex-col p-2 md:p-3 rounded-lg border transition-all ${
                  today ? 'border-primary bg-primary/5' : 'border-border bg-card'
                }`}
              >
                <div className="text-center mb-2">
                  <span className="text-xs text-muted-foreground block">{dayName}</span>
                  <span className={`text-lg md:text-xl font-semibold ${
                    today ? 'text-primary' : 'text-foreground'
                  }`}>{dayNumber}</span>
                </div>

                {totalItems > 0 && (
                  <div className="space-y-1">
                    {maintenanceItems?.slice(0, 2)?.map((wo) => (
                      <div
                        key={wo?.id}
                        className={`text-xs p-1.5 rounded border ${getPriorityColor(wo?.priority)}`}
                      >
                        <div className="font-medium truncate">{wo?.title}</div>
                      </div>
                    ))}
                    {equipmentItems?.slice(0, 1)?.map((eq) => (
                      <div
                        key={eq?.id}
                        className="text-xs p-1.5 rounded border bg-accent/10 text-accent border-accent"
                      >
                        <div className="font-medium truncate">{eq?.name}</div>
                      </div>
                    ))}
                    {totalItems > 3 && (
                      <div className="text-xs text-muted-foreground text-center pt-1">
                        +{totalItems - 3} more
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-card rounded-xl border border-border p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Icon name="Info" size={16} />
          Priority Levels
        </h3>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-error"></div>
            <span className="text-xs text-muted-foreground">Urgent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-warning"></div>
            <span className="text-xs text-muted-foreground">High</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-primary"></div>
            <span className="text-xs text-muted-foreground">Medium</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-muted"></div>
            <span className="text-xs text-muted-foreground">Low</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-accent"></div>
            <span className="text-xs text-muted-foreground">Preventive</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceCalendar;